import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Switch } from "../ui/switch";
import { Progress } from "../ui/progress";
import { useToast } from "../../hooks/use-toast";
import { Layers } from "lucide-react";

export default function ComplianceFrameworks({ isAdmin, onViewControls }) {
  const { toast } = useToast();
  const [frameworks, setFrameworks] = useState([]);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fwRes, ctRes] = await Promise.all([
        api.get("/compliance/frameworks"),
        api.get("/compliance/controls"),
      ]);
      setFrameworks(fwRes.data.frameworks || []);
      setControls(ctRes.data.controls || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (fw, enabled) => {
    setFrameworks((prev) => prev.map((f) => (f.id === fw.id ? { ...f, enabled } : f)));
    try {
      await api.put(`/compliance/frameworks/${fw.id}`, { enabled });
    } catch (e) {
      toast({ title: "Couldn't update framework", description: e?.response?.data?.detail || "", variant: "destructive" });
      setFrameworks((prev) => prev.map((f) => (f.id === fw.id ? { ...f, enabled: !enabled } : f)));
    }
  };

  const readinessFor = (fwKey) => {
    const scoped = controls.filter((c) => c.frameworks?.includes(fwKey));
    if (scoped.length === 0) return 0;
    const healthy = scoped.filter((c) => c.health === "healthy").length;
    return Math.round((healthy / scoped.length) * 100);
  };

  const controlsCountFor = (fwKey) => controls.filter((c) => c.frameworks?.includes(fwKey)).length;

  if (loading) return <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-xl bg-muted/40 animate-pulse" />)}</div>;

  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="compliance-frameworks-grid">
      {frameworks.map((fw) => {
        const pct = readinessFor(fw.key);
        const controlCount = controlsCountFor(fw.key);
        return (
          <div
            key={fw.id}
            role="button"
            tabIndex={0}
            onClick={() => onViewControls?.(fw.key)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onViewControls?.(fw.key); }}
            className="rounded-xl border border-border bg-card p-4 cursor-pointer hover:border-primary/40 transition-colors"
            data-testid={`compliance-framework-${fw.key}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                  <Layers className="w-4.5 h-4.5 text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold">{fw.name}</div>
                  <div className="text-[11.5px] text-muted-foreground mt-0.5">{fw.description}</div>
                </div>
              </div>
              {isAdmin && (
                <Switch
                  checked={!!fw.enabled}
                  onCheckedChange={(v) => toggle(fw, v)}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`compliance-framework-toggle-${fw.key}`}
                />
              )}
            </div>
            {fw.enabled && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11.5px] mb-1">
                  <span className="text-muted-foreground">{fw.requirements} requirements</span>
                  <span className="font-semibold">{pct}% ready</span>
                </div>
                <Progress value={pct} className="h-1.5" />
              </div>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onViewControls?.(fw.key); }}
              className="text-[12px] font-medium text-primary hover:underline mt-3"
              data-testid={`compliance-framework-view-controls-${fw.key}`}
            >
              View {controlCount} control{controlCount === 1 ? "" : "s"} →
            </button>
          </div>
        );

      })}
    </div>
  );
}
